/**
 * The shape of a state-exam listening recording, measured.
 *
 * The board publishes every sitting's recording, so unlike telc there is no
 * mock exam to squint at: this is the real thing. Every number below was
 * measured by `scripts/telc-audio/map-track.mjs --root .sources/certyfikat/…`
 * off the June 2024 recordings, B1 (26:11) and B2 (35:59), which times the
 * track with ffmpeg and only asks a transcriber what was said.
 * `scripts/certyfikat-audio/README.md` has the raw findings.
 *
 * **The two levels share one frame.** The same announcer's furniture, the same
 * signal, the same minute to look through the paper and the same minute to
 * check it. What differs is the level's name in the opening and how often each
 * task is played — which is what each task's instruction says, and is read
 * from there rather than restated here.
 *
 * **A task played twice is played twice whole.** Not each recording twice in a
 * row: the example and every numbered recording run through, 46 seconds pass,
 * the signal sounds, and the whole sequence runs again. Those 46 seconds are
 * the time to answer; the gaps inside a pass are only long enough to find the
 * next question.
 */
import type { Level } from "./exam-family.ts";

/**
 * The signal before every pass: two notes a fourth apart, left to ring.
 *
 * Measured at 350 Hz (with 175 and 700 under and over it) and, a second in,
 * 467 Hz (with 233 and 933), dying away over 4.25 s. Synthesised from these
 * numbers — the shape of the exam is a fact about how it is administered, the
 * board's recording is the board's.
 */
export const CERT_SIGNAL = {
  seconds: 4.25,
  partials: [
    { hz: 175, db: -11, at: 0 },
    { hz: 350, db: 0, at: 0 },
    { hz: 700, db: -9, at: 0 },
    { hz: 233, db: -3, at: 1 },
    { hz: 467, db: 0, at: 1 },
    { hz: 933, db: -10, at: 1 },
  ],
  fadeSeconds: 0.02,
  /**
   * How fast it dies away: the measured envelope falls from −23 to −48 dB over
   * the four seconds, evenly in decibels — a struck bell, not a beep. A curve
   * steeper at the start lets the first note vanish before the second sounds,
   * and the signal then measures as two.
   */
  decay: 6,
} as const;

/** Every pause on the track, in seconds. */
export const CERT_TRACK = {
  leadIn: 0.9,
  /** Between two sentences of one announcement. */
  betweenSentences: 0.9,
  /** "Przed każdym nagraniem usłyszą Państwo sygnał." — and then it does. */
  afterSignalDemo: 1.6,
  /** "Proszę teraz obejrzeć cały test." */
  lookThrough: 60.5,
  /** Before "Zadanie pierwsze", after "Zaczynamy test". */
  beforeTask: 0.6,
  /** After the instruction and its "Uwaga! …", before the signal. */
  afterInstruction: 10.3,
  /** From the end of the signal to the first word. */
  afterSignal: 0.7,
  /** From "Przykład" or "Jeden" to the recording. */
  afterLabel: 2.25,
  /**
   * After one recording and before the next one's label.
   *
   * It depends on what was just heard, and the measurement gives four values
   * that do not overlap: a single sentence leaves 6.4 s (B1 task I), a two-line
   * exchange 4.5 s (B1 task II), a person's opinion 5.5 s (B1 task V), and a
   * conversation 6.3 s (B2 task I). The lettered sections of one text (B2's
   * four beaches) run on after 2 s — they are one recording read in parts.
   */
  between: { sentence: 6.4, exchange: 4.5, opinion: 5.5, conversation: 6.3, section: 2.0 },
  /** After the first pass of a task played twice: the time to answer. */
  betweenPasses: 46,
  /** After the last pass, before "Koniec zadania …". */
  afterTask: 10.3,
  /** After "Koniec zadania …", before the next task. */
  afterClose: 2.2,
  /** "Proszę teraz sprawdzić cały test." */
  checkThrough: 60.1,
  tail: 1.5,
} as const;

/**
 * What a level is called in the opening, as the board's announcer says it —
 * spelled as it is spoken, because the synthesiser refuses a line with a digit
 * in it (`speak()` in `scripts/audio/engine.ts`).
 */
const LEVEL_NAMES: Partial<Record<Level, string>> = {
  A2: "Poziom podstawowy a dwa.",
  B1: "Poziom progowy be jeden.",
  B2: "Poziom średni ogólny be dwa.",
  C1: "Poziom zaawansowany ce jeden.",
  C2: "Poziom biegły ce dwa.",
};

const TASKS = ["pierwsze", "drugie", "trzecie", "czwarte", "piąte", "szóste", "siódme"];
const TASKS_GEN = ["pierwszego", "drugiego", "trzeciego", "czwartego", "piątego", "szóstego", "siódmego"];
const COUNT = ["", "jedno", "dwa", "trzy", "cztery", "pięć", "sześć", "siedem"];
const NUMBERS = [
  "", "Jeden", "Dwa", "Trzy", "Cztery", "Pięć", "Sześć", "Siedem", "Osiem", "Dziewięć",
  "Dziesięć", "Jedenaście", "Dwanaście", "Trzynaście", "Czternaście", "Piętnaście",
];

/**
 * The announcer's furniture, in the board's own words.
 *
 * Unlike telc's, these are not ours to reword: the state exam's announcements
 * are a published administrative procedure, identical on every sitting, and a
 * candidate preparing for it should hear exactly what they will hear. The
 * opening does not name the board — these sets are not the board's, and a
 * recording that opened by saying so would be the lie the Writing card is not
 * allowed to tell.
 */
export const CERT_WORDS = {
  opening: (level: Level, tasks: number): string[] => {
    const name = LEVEL_NAMES[level];
    if (!name) throw new Error(`${level}: the state exam's announcer has no name for this level`);
    const count = COUNT[tasks];
    if (!count) throw new Error(`${tasks} tasks: no word for that many`);
    return [
      "Egzamin certyfikatowy z języka polskiego jako obcego.",
      name,
      "Rozumienie ze słuchu.",
      `${count[0]!.toUpperCase()}${count.slice(1)} ${tasks >= 5 ? "zadań" : "zadania"}.`,
      "Proszę uważnie słuchać nagrań.",
      "Przerwy między nagraniami proszę wykorzystać na zapoznanie się z zadaniami oraz na poprawę błędów i zastanowienie się nad brakującymi informacjami.",
      "Przed każdym nagraniem usłyszą Państwo sygnał.",
    ];
  },
  lookThrough: ["Uwaga!", "Proszę teraz obejrzeć cały test."],
  start: ["Uwaga!", "Zaczynamy test."],
  task: (index: number): string => {
    const word = TASKS[index];
    if (!word) throw new Error(`task ${index + 1}: no word for it`);
    return `Zadanie ${word}.`;
  },
  /** Read before every task, whatever its printed instruction says after it. */
  listen: "Proszę uważnie słuchać tego nagrania i wykonywać zadanie zgodnie z podanym przykładem.",
  /**
   * How often the recording plays, as the announcer says it — and the levels
   * say it differently: B1's "dwa razy" is B2's "dwukrotnie", on the recording
   * and in the printed instruction alike.
   */
  plays: (level: Level, plays: 1 | 2): string[] => [
    "Uwaga!",
    plays === 1
      ? "Nagranie zostanie odtworzone tylko jeden raz."
      : `Nagranie zostanie odtworzone ${level === "B1" || level === "A2" ? "dwa razy" : "dwukrotnie"}.`,
  ],
  example: "Przykład.",
  close: (index: number): string => {
    const word = TASKS_GEN[index];
    if (!word) throw new Error(`task ${index + 1}: no word for it`);
    return `Koniec zadania ${word}.`;
  },
  checkThrough: "Proszę teraz sprawdzić cały test.",
  end: "Koniec części Rozumienie ze słuchu.",
} as const;

/**
 * How the announcer reads a recording's label.
 *
 * Numbers as words — "Osoba 1" is announced "Jeden", exactly as the board does
 * — and a letter as itself. Anything else is not something the announcer says.
 */
export function spokenLabel(label: string): string | null {
  const trimmed = label.trim();
  if (/^przykład/iu.test(trimmed)) return CERT_WORDS.example;
  const number = /(\d+)\.?$/u.exec(trimmed.replace(/[.:]$/u, ""));
  if (number) {
    const word = NUMBERS[Number(number[1])];
    if (!word) throw new Error(`${label}: no word for that number`);
    return `${word}.`;
  }
  const letter = /^([A-H])(?:\.|$)/u.exec(trimmed);
  if (letter) return `${letter[1]}.`;
  return null;
}

/**
 * What kind of recording just finished, for the pause after it.
 *
 * Read off the recording itself, the same way the measurement found the four
 * values: how many people speak, and how much they say.
 */
export function pauseAfter(
  recording: { label: string; turns: { speaker: string; text: string }[] },
  next?: { label: string },
): number {
  // The lettered sections of one text, and the introduction that leads into
  // the first of them, run on: "…skradną ich serca. A. Plaża Kleftiko".
  if (/^[A-H](\.|$)/u.test(recording.label.trim())) return CERT_TRACK.between.section;
  if (next && /^[A-H](\.|$)/u.test(next.label.trim())) return CERT_TRACK.between.section;
  const words = recording.turns.reduce((n, t) => n + t.text.split(/\s+/u).length, 0);
  const speakers = new Set(recording.turns.map((t) => t.speaker)).size;
  if (speakers === 1) return words <= 25 ? CERT_TRACK.between.sentence : CERT_TRACK.between.opinion;
  return words <= 40 ? CERT_TRACK.between.exchange : CERT_TRACK.between.conversation;
}
